import emptyRoom from "./emptyRoom";
import validateRoom from './roomValidation';

/*
* This function is a simple algorithm designed to automatically assign debaters to debate rooms
*/
export default function smartAssignment(seniorPosition, history, debatersToAssign, rooms) {
    let positions = ["Opening Government", "Opening Opposition", "Closing Government", "Closing Opposition"];
    let positionQueues = {};
    let positionPairs = {};

    let juniors = debatersToAssign
        .filter(member => member.rank === "Junior" && member.student && (member.registration === undefined || member.registration.cancelled !== true))
        .sort(function (fst, snd) {
            let fstParticipationCount = Object.values(history[fst.uid].events).filter(event => positions.indexOf(event.position) !== -1).length;
            let sndParticipationCount = Object.values(history[snd.uid].events).filter(event => positions.indexOf(event.position) !== -1).length;
            return fstParticipationCount - sndParticipationCount;
        });

    let judgementMap = {S1: 5, C2: 4, C1: 3, W3: 2, W2: 1, W1: 0};
    let seniors = debatersToAssign.filter(debater => debater.rank !== "Junior" && (debater.registration === undefined || debater.registration.cancelled !== true))
        .sort(function (a, b) {
            if (a.start_year.startsWith("Dinosaur")) {
                return -1;
            }
            if (b.start_year.startsWith("Dinosaur")) {
                return 1;
            }
            return a.start_year.localeCompare(b.start_year);
        })
        .sort(function (a, b) {
            return judgementMap[b.official_judgement_level] - judgementMap[a.official_judgement_level];
        });

    let roomsCount;
    if (seniorPosition === "None") {
        roomsCount = Math.min(seniors.length, Math.floor(juniors.length / 8));
    } else {
        roomsCount = Math.min(Math.floor(seniors.length / 2), Math.floor(juniors.length / 7));
    }

    positions.forEach(position => positionQueues[position] = []);

    juniors.forEach(junior => {
        let juniorPriorities = [];
        positions.forEach(position => {
            juniorPriorities.push({
                position: position,
                count: Object.values(history[junior.uid].events).filter(event => event.position === position).length
            });
        });
        juniorPriorities.sort(function (a, b) {
            return a.count - b.count;
        });
        let juniorAssigned = false;
        for (let i = 0; i < juniorPriorities.length && !juniorAssigned; i++) {
            let maxMembersForPosition = seniorPosition === juniorPriorities[i].position ? roomsCount : roomsCount * 2;
            if (positionQueues[juniorPriorities[i].position].length < maxMembersForPosition) {
                positionQueues[juniorPriorities[i].position].push(junior);
                juniorAssigned = true;
            }
        }
        if (juniorAssigned) {
            let index = debatersToAssign.findIndex(jr => jr.uid === junior.uid);
            if (index > -1) {
                debatersToAssign.splice(index, 1);
            }
        }
    });

    positions.forEach(position => {
        positionPairs[position] = [];
        if (position === seniorPosition) {
            return;
        }
        while (positionQueues[position].length > 0) {
            let firstJunior = positionQueues[position].shift();
            let partner = positionQueues[position].sort(function (a, b) {
                let fstCount = Object.entries(history[firstJunior.uid].events).filter(event => {
                    return history[a.uid].events[event[0]] !== undefined
                        && history[a.uid].events[event[0]].chair_id !== undefined
                        && history[a.uid].events[event[0]].position !== undefined
                        && history[a.uid].events[event[0]].chair_id === event[1].chair_id
                        && history[a.uid].events[event[0]].position === event[1].position
                }).length;

                let sndCount = Object.entries(history[firstJunior.uid].events).filter(event => {
                    return history[b.uid].events[event[0]] !== undefined
                        && history[b.uid].events[event[0]].chair_id !== undefined
                        && history[b.uid].events[event[0]].position !== undefined
                        && history[b.uid].events[event[0]].chair_id === event[1].chair_id
                        && history[b.uid].events[event[0]].position === event[1].position
                }).length;
                return fstCount - sndCount;
            })[0];
            let index = positionQueues[position].indexOf(partner);
            if (index > -1) {
                positionQueues[position].splice(index, 1);
            }
            positionPairs[position].push([firstJunior, partner]);
        }
    });

    if (seniorPosition !== "None") {
        positionQueues[seniorPosition].forEach(junior => {
            let senior = seniors.pop();
            positionPairs[seniorPosition].push([senior, junior]);
            let index = debatersToAssign.findIndex(member => member.uid === senior.uid);
            if (index > -1) {
                debatersToAssign.splice(index, 1);
            }
        });
        positionQueues[seniorPosition] = [];
    }

    rooms = [];
    for (let i = 0; i < roomsCount; i++) {
        let room = JSON.parse(JSON.stringify(emptyRoom));
        let chair = seniors.shift();
        room["Chair"] = [chair];
        let index = debatersToAssign.findIndex(member => member.uid === chair.uid);
        if (index > -1) {
            debatersToAssign.splice(index, 1);
        }
        positions.forEach(position => {
            let chairMatchingPair = positionPairs[position].sort(function (a, b) {
                let aSum = 0, aCount = 0, bSum = 0, bCount = 0;
                a.forEach(member => {
                    if (member.rank === "Junior") {
                        aSum += Object.values(history[member.uid].events).filter(event => event.chair_id === chair.uid).length;
                        aCount++;
                    }
                });
                b.forEach(member => {
                    if (member.rank === "Junior") {
                        bSum += Object.values(history[member.uid].events).filter(event => event.chair_id === chair.uid).length;
                        bCount++;
                    }
                });
                let aAvg = aCount !== 0 ? aSum / aCount : 0;
                let bAvg = bCount !== 0 ? bSum / bCount : 0;
                return aAvg - bAvg;
            })[0];
            let index = positionPairs[position].indexOf(chairMatchingPair);
            if (index > -1) {
                positionPairs[position].splice(index, 1);
            }
            chairMatchingPair.forEach(member => room[position].push(member));
        });
        rooms.push(validateRoom(room));
    }
    return {debatersToAssign, rooms};
}